import React, { useState, useEffect } from 'react';
import { FiX as X, FiSave as Save, FiMessageCircle as MessageCircle } from 'react-icons/fi';
import HelpTooltip from '../../../../../components/common/HelpTooltip';

/**
 * Editor for Q&A pair-type dataset items
 * Used for fine-tuning data with question-answer pairs
 */

interface QATypeSpecificData {
  question: string;
  answer: string;
  context: string;
  instruction: string;
}

interface QAMetadata {
  tags: string[];
  difficulty: string;
  category: string;
}

interface QAFormData {
  name: string;
  content: string;
  type_specific_data: QATypeSpecificData;
  metadata: QAMetadata;
  embedding_text: string;
}

interface DatasetItem {
  name?: string;
  content?: string;
  type_specific_data?: Partial<QATypeSpecificData>;
  metadata?: Partial<QAMetadata>;
  embedding_text?: string;
}

interface SavePayload {
  item_type: string;
  content: string;
  name: string;
  type_specific_data: QATypeSpecificData;
  metadata: QAMetadata;
  embedding_text: string | null;
}

interface QAPairItemEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (payload: SavePayload) => void;
  item?: DatasetItem | null;
  saving?: boolean;
}

const QAPairItemEditor: React.FC<QAPairItemEditorProps> = ({
  isOpen,
  onClose,
  onSave,
  item = null,
  saving = false,
}) => {
  const [formData, setFormData] = useState<QAFormData>({
    name: '',
    content: '',
    type_specific_data: {
      question: '',
      answer: '',
      context: '',
      instruction: '',
    },
    metadata: {
      tags: [],
      difficulty: 'medium',
      category: '',
    },
    embedding_text: '',
  });

  const [tagsInput, setTagsInput] = useState('');

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        content: item.content || '',
        type_specific_data: {
          question: item.type_specific_data?.question || '',
          answer: item.type_specific_data?.answer || '',
          context: item.type_specific_data?.context || '',
          instruction: item.type_specific_data?.instruction || '',
        },
        metadata: {
          tags: item.metadata?.tags || [],
          difficulty: item.metadata?.difficulty || 'medium',
          category: item.metadata?.category || '',
        },
        embedding_text: item.embedding_text || '',
      });
      setTagsInput((item.metadata?.tags || []).join(', '));
    } else {
      setFormData({
        name: '',
        content: '',
        type_specific_data: { question: '', answer: '', context: '', instruction: '' },
        metadata: { tags: [], difficulty: 'medium', category: '' },
        embedding_text: '',
      });
      setTagsInput('');
    }
  }, [item, isOpen]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const tags = tagsInput?.split(',').map((t) => t.trim()).filter((t) => t) ?? [];
    const combinedContent = `Q: ${formData.type_specific_data.question}\n\nA: ${formData.type_specific_data.answer}`;

    onSave({
      item_type: 'qa_pair',
      content: combinedContent,
      name:
        formData.name ||
        formData.type_specific_data.question?.substring(0, 50) ||
        'Untitled Q&A',
      type_specific_data: formData.type_specific_data,
      metadata: {
        ...formData.metadata,
        tags,
        difficulty: formData.metadata.difficulty,
      },
      embedding_text: formData.embedding_text || null,
    });
  };

  if (!isOpen) return null;

  let submitButtonLabel: string;
  if (saving) {
    submitButtonLabel = 'Saving...';
  } else if (item) {
    submitButtonLabel = 'Update';
  } else {
    submitButtonLabel = 'Create';
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-green-600" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {item ? 'Edit Q&A Pair' : 'Add Q&A Pair'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name (optional)
              </label>
              <input id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors"
                placeholder="e.g., Password Reset FAQ"
              />
            </div>

            {/* Context (optional) */}
            <div>
              <label htmlFor="context" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Context (optional)
                <HelpTooltip text="Background information or reference material that helps frame the question. Useful for domain-specific Q&A where the LLM needs context to answer correctly." />
              </label>
              <textarea id="context"
                value={formData.type_specific_data.context}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    type_specific_data: { ...formData.type_specific_data, context: e.target.value },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors"
                rows={2}
                placeholder="Background information or context for this Q&A..."
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Additional context that helps understand the question
              </p>
            </div>

            {/* Question */}
            <div>
              <label htmlFor="question" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Question *
                <HelpTooltip text="The user query or prompt that will be sent to the LLM during evaluation." />
              </label>
              <textarea id="question"
                required
                value={formData.type_specific_data.question}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    type_specific_data: { ...formData.type_specific_data, question: e.target.value },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors"
                rows={3}
                placeholder="Enter the question..."
              />
            </div>

            {/* Answer */}
            <div>
              <label htmlFor="answer" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Answer *
                <HelpTooltip text="The ideal response used for scoring. The LLM's actual output will be compared against this answer." />
              </label>
              <textarea id="answer"
                required
                value={formData.type_specific_data.answer}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    type_specific_data: { ...formData.type_specific_data, answer: e.target.value },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors"
                rows={5}
                placeholder="Enter the expected answer..."
              />
            </div>

            {/* Instruction (for instruction tuning) */}
            <div>
              <label htmlFor="system-instruction" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                System Instruction (optional)
                <HelpTooltip text="A system-level instruction prepended to the question during evaluation. Use to set the LLM's persona or response style." />
              </label>
              <textarea id="system-instruction"
                value={formData.type_specific_data.instruction}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    type_specific_data: {
                      ...formData.type_specific_data,
                      instruction: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors"
                rows={2}
                placeholder="e.g., You are a helpful customer support agent..."
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                System prompt to use for instruction tuning
              </p>
            </div>

            {/* Difficulty & Category Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Difficulty
                </label>
                <select id="difficulty"
                  value={formData.metadata.difficulty}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      metadata: { ...formData.metadata, difficulty: e.target.value },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <input id="category"
                  type="text"
                  value={formData.metadata.category}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      metadata: { ...formData.metadata, category: e.target.value },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 transition-colors"
                  placeholder="e.g., Support, Sales"
                />
              </div>
            </div>

            {/* Tags */}
            <div>
              <label htmlFor="tags-comma-separated" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tags (comma-separated)
              </label>
              <input id="tags-comma-separated"
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors"
                placeholder="e.g., billing, refund, policy"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                       transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg
                       hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors"
            >
              <Save className="w-4 h-4" />
              {submitButtonLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QAPairItemEditor;
